"use client";

/**
 * Shared shells, format helpers and small in-house SVG charts for the
 * Account Analytics modal (proposal 70). No charting dependency — everything
 * renders with design-system tokens (price roles: blue-400 win / red-400 loss).
 */

import React, { useMemo } from "react";
import { cn } from "@/lib/utils";

// ───────────────────────────────────────────── formatting helpers ──

export function fmtMoney(value: number, currency?: string): string {
  const s = value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return currency ? `${s} ${currency}` : s;
}

export function fmtSignedMoney(value: number, currency?: string): string {
  const sign = value > 0 ? "+" : "";
  return `${sign}${fmtMoney(value, currency)}`;
}

export function fmtPct(value: number | null | undefined, digits = 2): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return "—";
  }
  return `${value.toFixed(digits)}%`;
}

export function fmtSignedPct(value: number | null | undefined, digits = 2): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return "—";
  }
  return `${value > 0 ? "+" : ""}${value.toFixed(digits)}%`;
}

export function fmtPips(value: number, digits = 1): string {
  return `${value > 0 ? "+" : ""}${value.toFixed(digits)}`;
}

export function fmtRatio(value: number | null): string {
  if (value === null || !Number.isFinite(value)) {
    return value === Infinity ? "∞" : "—";
  }
  return value.toFixed(2);
}

export function pnlClass(value: number | null | undefined): string {
  if (value === null || value === undefined || value === 0) {
    return "text-foreground";
  }
  return value > 0 ? "text-blue-400" : "text-red-400";
}

export function fmtDayMonth(ms: number | null): string {
  if (!ms || !Number.isFinite(ms)) return "—";
  const d = new Date(ms);
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export const MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// ─────────────────────────────────────────────────── card shells ──

export function AnalyticsCard({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "rounded-md border border-border bg-card overflow-hidden",
        className
      )}
    >
      {children}
    </div>
  );
}

export function AnalyticsCardHeader({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "h-7 shrink-0 px-2 flex items-center gap-1 border-b border-border",
        "text-[10px] uppercase tracking-wide text-muted-foreground",
        className
      )}
    >
      {children}
    </div>
  );
}

export function AnalyticsTab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "px-2 py-0.5 rounded text-[10px] uppercase tracking-wide transition-colors",
        active
          ? "bg-accent text-foreground"
          : "text-muted-foreground hover:text-foreground"
      )}
    >
      {children}
    </button>
  );
}

export function StatRow({
  label,
  value,
  valueClass,
}: {
  label: string;
  value: React.ReactNode;
  valueClass?: string;
}) {
  return (
    <div className="flex items-center justify-between gap-2 py-[3px] border-b border-border/40 last:border-b-0 text-[11px]">
      <span className="text-muted-foreground truncate">{label}</span>
      <span
        className={cn(
          "font-mono tabular-nums text-right whitespace-nowrap",
          valueClass
        )}
      >
        {value}
      </span>
    </div>
  );
}

/** Won-vs-lost split bar (Profitability). */
export function WinLossSplitBar({
  winPct,
  className,
}: {
  winPct: number;
  className?: string;
}) {
  const w = Math.max(0, Math.min(100, winPct));
  return (
    <span
      className={cn(
        "inline-flex h-2 w-24 overflow-hidden rounded-sm border border-border align-middle",
        className
      )}
    >
      <span className="h-full bg-blue-400" style={{ width: `${w}%` }} />
      <span className="h-full bg-red-400" style={{ width: `${100 - w}%` }} />
    </span>
  );
}

// ─────────────────────────────────────────────── SVG chart pieces ──

export interface LinePoint {
  t: number;
  v: number;
}

export interface ColumnPoint {
  t: number;
  v: number;
}

const CHART_W = 640;
const CHART_H = 160;
const MAX_LINE_POINTS = 400;

function sample<T>(points: T[], cap: number): T[] {
  if (points.length <= cap) return points;
  const out: T[] = [];
  const step = (points.length - 1) / (cap - 1);
  for (let i = 0; i < cap; i++) {
    out.push(points[Math.round(i * step)]);
  }
  return out;
}

/**
 * Equity spline + column overlay (Growth/Balance/Profit chart).
 * Line uses the win price role; columns are lots/day or daily P&L
 * (blue positive / red negative, low opacity) per the proposal.
 */
export function SplineColumnChart({
  line,
  columns,
  signedColumns = false,
  height = CHART_H,
}: {
  line: LinePoint[];
  columns: ColumnPoint[];
  /** color columns by sign (daily P&L) instead of neutral volume columns */
  signedColumns?: boolean;
  height?: number;
}) {
  const built = useMemo(() => {
    if (line.length === 0) return null;
    const pts = sample(line, MAX_LINE_POINTS);
    const t0 = pts[0].t;
    const t1 = pts[pts.length - 1].t;
    const span = Math.max(1, t1 - t0);
    let vMin = Infinity;
    let vMax = -Infinity;
    for (const p of pts) {
      if (p.v < vMin) vMin = p.v;
      if (p.v > vMax) vMax = p.v;
    }
    if (!Number.isFinite(vMin) || !Number.isFinite(vMax)) return null;
    if (vMax === vMin) {
      vMax += 1;
      vMin -= 1;
    }
    const pad = (vMax - vMin) * 0.06;
    vMin -= pad;
    vMax += pad;

    const x = (t: number) => ((t - t0) / span) * CHART_W;
    const y = (v: number) =>
      CHART_H - ((v - vMin) / (vMax - vMin)) * (CHART_H - 8) - 4;

    let d = "";
    pts.forEach((p, i) => {
      d += `${i === 0 ? "M" : "L"}${x(p.t).toFixed(1)},${y(p.v).toFixed(1)}`;
    });
    const area = `${d}L${CHART_W},${CHART_H}L0,${CHART_H}Z`;

    const colMaxAbs = columns.reduce((m, c) => Math.max(m, Math.abs(c.v)), 0);
    const colZone = CHART_H * 0.3;
    const colW = Math.max(
      1,
      Math.min(8, (CHART_W / Math.max(1, columns.length)) * 0.6)
    );
    const cols = columns.map((c) => ({
      x: x(c.t) - colW / 2,
      h: colMaxAbs > 0 ? (Math.abs(c.v) / colMaxAbs) * colZone : 0,
      positive: c.v >= 0,
    }));

    return { d, area, cols, colW };
  }, [line, columns]);

  if (!built) {
    return (
      <div
        className="flex items-center justify-center text-[11px] text-muted-foreground"
        style={{ height }}
      >
        Not enough data
      </div>
    );
  }

  return (
    <svg
      viewBox={`0 0 ${CHART_W} ${CHART_H}`}
      preserveAspectRatio="none"
      className="block w-full"
      style={{ height }}
      aria-hidden
    >
      {built.cols.map((c, i) => (
        <rect
          key={i}
          x={c.x}
          y={CHART_H - c.h}
          width={built.colW}
          height={c.h}
          className={
            signedColumns
              ? c.positive
                ? "fill-blue-400/30"
                : "fill-red-400/30"
              : "fill-muted-foreground/25"
          }
        />
      ))}
      <path d={built.area} className="fill-blue-400/10" />
      <path
        d={built.d}
        className="stroke-blue-400"
        fill="none"
        strokeWidth={1.5}
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

/** Winners-vs-losers grouped bars (Hourly / Daily distributions). */
export function GroupedWinLossBars({
  buckets,
  labels,
}: {
  buckets: { key: number; winners: number; losers: number }[];
  labels: (key: number) => string;
}) {
  const max = buckets.reduce(
    (m, b) => Math.max(m, b.winners, b.losers),
    0
  );
  return (
    <div>
      <div className="flex items-end gap-1 h-28 px-2 pt-2">
        {buckets.map((b) => (
          <div
            key={b.key}
            className="flex-1 flex items-end justify-center gap-px h-full"
            title={`${labels(b.key)} — ${b.winners} won / ${b.losers} lost`}
          >
            <div
              className="w-1.5 min-w-1 rounded-t-sm bg-blue-400"
              style={{
                height: max > 0 ? `${(b.winners / max) * 100}%` : 0,
              }}
            />
            <div
              className="w-1.5 min-w-1 rounded-t-sm bg-red-400"
              style={{
                height: max > 0 ? `${(b.losers / max) * 100}%` : 0,
              }}
            />
          </div>
        ))}
      </div>
      <div className="flex gap-1 px-2 pb-1.5 pt-1">
        {buckets.map((b) => (
          <span
            key={b.key}
            className="flex-1 text-center font-mono text-[9px] text-muted-foreground"
          >
            {labels(b.key)}
          </span>
        ))}
      </div>
      <div className="flex items-center gap-3 px-2 pb-2 text-[9px] text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <span className="h-2 w-2 rounded-sm bg-blue-400" /> winners
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="h-2 w-2 rounded-sm bg-red-400" /> losers
        </span>
      </div>
    </div>
  );
}

/** Duration-vs-pips scatter (winners blue, losers red). */
export function DurationScatter({
  points,
  formatDuration,
}: {
  points: { durationMs: number; pips: number; win: boolean }[];
  formatDuration: (ms: number) => string;
}) {
  const W = 640;
  const H = 150;
  const built = useMemo(() => {
    if (points.length === 0) return null;
    const maxDur = points.reduce((m, p) => Math.max(m, p.durationMs), 0);
    const maxAbsPips = points.reduce(
      (m, p) => Math.max(m, Math.abs(p.pips)),
      0
    );
    if (maxDur <= 0 && maxAbsPips <= 0) return null;
    const x = (dur: number) =>
      maxDur > 0 ? 6 + (dur / maxDur) * (W - 12) : W / 2;
    const y = (pips: number) =>
      maxAbsPips > 0
        ? H / 2 - (pips / maxAbsPips) * (H / 2 - 8)
        : H / 2;
    return {
      dots: points.map((p) => ({ cx: x(p.durationMs), cy: y(p.pips), win: p.win })),
      maxDur,
    };
  }, [points]);

  if (!built) {
    return (
      <div className="flex h-[150px] items-center justify-center text-[11px] text-muted-foreground">
        Not enough data
      </div>
    );
  }

  return (
    <div className="px-2 pb-2 pt-1">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        className="block w-full border-b border-l border-border"
        style={{ height: H }}
        aria-hidden
      >
        <line
          x1={0}
          x2={W}
          y1={H / 2}
          y2={H / 2}
          className="stroke-border"
          strokeDasharray="4 4"
        />
        {built.dots.map((d, i) => (
          <circle
            key={i}
            cx={d.cx}
            cy={d.cy}
            r={2.4}
            className={d.win ? "fill-blue-400/80" : "fill-red-400/80"}
          />
        ))}
      </svg>
      <div className="flex items-center justify-between pt-1 font-mono text-[9px] text-muted-foreground">
        <span>x · trade duration (0 → {formatDuration(built.maxDur)})</span>
        <span>y · pips (dashed = 0)</span>
      </div>
    </div>
  );
}
